import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getAppSurfaceBg, getAppShellTextColor } from '../theme/surfaces';
import TopBar from './TopBarV2';

export const PrivateRoute = ({ children, excludeRoles = [], hideLayout = false }) => {
  const { currentUser, userRole, userLandingPage, loading, initializing } = useAuth();
  const { isDarkMode, mainBgColor, secondaryColor } = useTheme();
  const location = useLocation();

  const resolvedBg = getAppSurfaceBg(isDarkMode, mainBgColor);
  const shellText = getAppShellTextColor(isDarkMode, secondaryColor);

  // If still initializing, show loading spinner
  if (initializing) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: resolvedBg,
          color: shellText
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not logged in, redirect to login
  if (!loading && !currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user role is excluded, redirect to user's landing page
  if (!loading && excludeRoles.includes(userRole)) {
    return <Navigate to={userLandingPage || '/dashboard'} replace />;
  }

  // Show loading spinner while still loading
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: resolvedBg,
          color: shellText
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (hideLayout) {
    return children;
  }

  // Render the protected content
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: resolvedBg,
        color: shellText
      }}
    >
      <TopBar />
      <Box
        component="main"
        sx={{
          flex: 1,
          width: '100%',
          maxWidth: '1600px',
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 2.5, md: 3 },
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
};
